import { t } from '@app/i18n';
import * as yup from 'yup';
import { MAX_HEADERS, PROTOCOLS, type ChatEntryForm, type HeaderForm } from './ChatEntryModel';

/**
 * Validation of the drawer «Entrada do chat» (aim-5wc).
 *
 * THE SAME RULES AS THE BACKEND, WITH THE SAME CODES (`domain/chat_entry.go` › ValidateChatEntry). Each rule is a yup
 * test NAMED after its field code, and its sentence is the code's in the catalog (`field.*`): a refusal reads the same
 * whether the form or the server caught it, and a test asserts the code (`error.type`), never the sentence. The backend
 * stays the authority — an API client never passes here, and whether the Service serves THIS identity is the
 * executor's check, at the test and at every question.
 */

/** `service.namespace`, two DNS-1123 labels in lowercase. */
export const SERVICE_PATTERN =
  /^[a-z0-9]([-a-z0-9]{0,61}[a-z0-9])?\.[a-z0-9]([-a-z0-9]{0,61}[a-z0-9])?$/;

/** The RFC 9110 token of a header name. */
export const HEADER_NAME_PATTERN = /^[!#$%&'*+.^_`|~0-9A-Za-z-]+$/;

/** The headers the Chat sets itself (§2.3). Compared without case. */
export const RESERVED_HEADERS = [
  'Authorization',
  'Cookie',
  'Host',
  'Content-Type',
  'Content-Length',
  'Accept',
  'A2A-Version',
  'X-Token-Pessoa',
  // The id of the question (aim-6ph.10): the executor sets it, and an extra with the same name would change it.
  'X-Aim-Conversation-Id',
];

export function reservedHeader(name: string): boolean {
  return RESERVED_HEADERS.some((reserved) => reserved.toLowerCase() === name.trim().toLowerCase());
}

/** Starts with `/`; no `//`, `@`, `#`, backslash, space or control character. A query is allowed. */
export function validPath(path: string): boolean {
  if (!path.startsWith('/') || path.length > 1024) return false;
  if (path.includes('//') || /[@#\\]/.test(path)) return false;
  // eslint-disable-next-line no-control-regex
  return !/[\u0000- \u007f]/.test(path);
}

/** A line break or another control character in a value would write a header of its own choosing. */
export function validHeaderValue(value: string): boolean {
  // eslint-disable-next-line no-control-regex
  return !/[\u0000-\u0008\u000a-\u001f\u007f]/.test(value);
}

const sentence = (code: string, params?: Record<string, string>) => t(`field.${code}`, params);

/** A function, not a module const: the sentences resolve `t()` in the language of the moment, never at import. */
function headerSchema() {
  return yup.object({
    name: yup
      .string()
      .defined()
      .test('chatEntryHeaderName', sentence('chatEntryHeaderName'), (value) => {
        const name = (value ?? '').trim();
        return HEADER_NAME_PATTERN.test(name) && name.length <= 128;
      }),
    sensitive: yup.boolean().defined(),
    value: yup
      .string()
      .defined()
      .test(
        'chatEntryHeaderValueRequired',
        sentence('chatEntryHeaderValueRequired'),
        function required(value) {
          const row = this.parent as HeaderForm;
          // A stored secret kept as it is: empty is «keep». A new one, or one being replaced, needs the value.
          const keeps = row.sensitive && row.storedHint !== null && !row.replacing;
          return keeps || Boolean(value);
        },
      )
      .test('chatEntryHeaderValueInvalid', sentence('chatEntryHeaderValueInvalid'), (value) =>
        validHeaderValue(value ?? ''),
      ),
    storedHint: yup.string().nullable().defined(),
    replacing: yup.boolean().defined(),
  });
}

/**
 * The rules that cross fields — a reserved name, the person token header repeated as an extra, a name twice — run
 * on the whole form, where both sides are known, and point the error at the header row (`headers[i].name`).
 */
function crossFields(
  this: yup.TestContext,
  form: ChatEntryForm | undefined,
): true | yup.ValidationError {
  if (!form) return true;
  const tokenHeader =
    form.tokenHeaderMode === 'authorization'
      ? 'authorization'
      : form.tokenHeaderName.trim().toLowerCase();
  const errors: yup.ValidationError[] = [];
  const seen = new Set<string>();
  form.headers.forEach((row, i) => {
    const name = row.name.trim();
    const lower = name.toLowerCase();
    if (!name) return;
    if (reservedHeader(name) || lower === tokenHeader) {
      errors.push(
        this.createError({
          path: `headers.${i}.name`,
          type: 'chatEntryHeaderReserved',
          message: sentence('chatEntryHeaderReserved', { name }),
        }),
      );
    } else if (seen.has(lower)) {
      errors.push(
        this.createError({
          path: `headers.${i}.name`,
          type: 'chatEntryHeaderDuplicate',
          message: sentence('chatEntryHeaderDuplicate', { name }),
        }),
      );
    }
    seen.add(lower);
  });
  if (form.tokenHeaderMode === 'other') {
    const name = form.tokenHeaderName.trim();
    if (!name) {
      errors.push(
        this.createError({
          path: 'tokenHeaderName',
          type: 'chatEntryTokenHeaderRequired',
          message: sentence('chatEntryTokenHeaderRequired'),
        }),
      );
    } else if (!HEADER_NAME_PATTERN.test(name)) {
      errors.push(
        this.createError({
          path: 'tokenHeaderName',
          type: 'chatEntryHeaderName',
          message: sentence('chatEntryHeaderName'),
        }),
      );
    } else if (reservedHeader(name) && name.toLowerCase() !== 'authorization') {
      errors.push(
        this.createError({
          path: 'tokenHeaderName',
          type: 'chatEntryHeaderReserved',
          message: sentence('chatEntryHeaderReserved', { name }),
        }),
      );
    }
  }
  return errors.length > 0 ? new yup.ValidationError(errors) : true;
}

export function chatEntrySchema(): yup.ObjectSchema<ChatEntryForm> {
  return yup
    .object({
      service: yup
        .string()
        .defined()
        .test('chatEntryServiceFormat', sentence('chatEntryServiceFormat'), (value) =>
          SERVICE_PATTERN.test((value ?? '').trim()),
        ),
      port: yup
        .string()
        .defined()
        .test('chatEntryPortRange', sentence('chatEntryPortRange'), (value) => {
          const text = (value ?? '').trim();
          if (!/^\d{1,5}$/.test(text)) return false;
          const port = Number(text);
          return port >= 1 && port <= 65535;
        }),
      path: yup
        .string()
        .defined()
        .test('chatEntryPathFormat', sentence('chatEntryPathFormat'), (value) =>
          validPath((value ?? '').trim()),
        ),
      protocol: yup
        .mixed<ChatEntryForm['protocol']>()
        .defined()
        .test('chatEntryProtocolInvalid', sentence('chatEntryProtocolInvalid'), (value) =>
          PROTOCOLS.includes(value as ChatEntryForm['protocol']),
        ) as yup.Schema<ChatEntryForm['protocol']>,
      tokenHeaderMode: yup
        .mixed<ChatEntryForm['tokenHeaderMode']>()
        .oneOf(['authorization', 'other'])
        .defined() as yup.Schema<ChatEntryForm['tokenHeaderMode']>,
      tokenHeaderName: yup.string().defined(),
      headers: yup
        .array()
        .of(headerSchema())
        .defined()
        .test(
          'chatEntryHeadersMax',
          sentence('chatEntryHeadersMax'),
          (list) => (list?.length ?? 0) <= MAX_HEADERS,
        ),
      opensChat: yup.boolean().defined(),
    })
    .test('chatEntryCrossFields', '', crossFields) as yup.ObjectSchema<ChatEntryForm>;
}
